import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../config/api';
import Layout from './Layout';
import OrdersTable from './tables/OrdersTable';
import Pagination from './common/Pagination';
import { useToast } from '../contexts/ToastContext';

interface Order {
  id: string;
  order_number: string;
  user_id: number;
  siteid: number;
  sitename: string;
  customer_name: string;
  customer_email: string;
  status: string;
  type: string;
  payment_status: string;
  payment_method?: string;
  payment_date?: string;
  po_number?: string;
  subtotal: number;
  shipping_cost: number;
  tax_amount: number;
  total_amount: number;
  notes?: string;
  reference?: string;
  created_at: string;
  updated_at: string;
  approved_at?: string;
  shipped_at?: string;
  delivered_at?: string;
}

interface OrderListResponse {
  items: Order[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

const OrderHistory: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalOrders, setTotalOrders] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string[]>([]);

  // Available filter options (computed from unfiltered data)
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [availablePaymentStatuses, setAvailablePaymentStatuses] = useState<string[]>([]);

  // Sorting
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Check if any filters are active (empty array means show all, so not active)
  const hasActiveFilters = statusFilter.length > 0 || paymentStatusFilter.length > 0;

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page: currentPage,
        page_size: 10,
        sort_by: sortBy,
        sort_order: sortOrder,
        include_quotes: false
      };

      // Add filters if set (backend expects arrays)
      if (statusFilter.length > 0) {
        params.status = statusFilter;
      }
      if (paymentStatusFilter.length > 0) {
        params.payment_status = paymentStatusFilter;
      }

      const response = await api.get<OrderListResponse>('/orders', { params });

      setOrders(response.data.items);
      setTotalOrders(response.data.total);
      setTotalPages(response.data.total_pages);
      setError('');

      // Compute available options from unfiltered data (only when no filters active)
      if (statusFilter.length === 0 && paymentStatusFilter.length === 0) {
        const statuses = new Set(response.data.items.map(order => order.status));
        const paymentStatuses = new Set(response.data.items.map(order => order.payment_status));
        setAvailableStatuses(Array.from(statuses).sort());
        setAvailablePaymentStatuses(Array.from(paymentStatuses).sort());
      }
    } catch (err: any) {
      console.error('Failed to fetch orders:', err);
      // Don't show error message to user
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, paymentStatusFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]); // Fetch when page/filters change

  const handleReorder = async (orderId: string) => {
    try {
      // Use the new backend reorder endpoint
      const response = await api.post(`/orders/${orderId}/reorder`);
      const result = response.data;

      // Show feedback to user
      if (result.items_added > 0) {
        showSuccess('Items added to cart successfully!');

        // Navigate to cart after a short delay
        setTimeout(() => {
          navigate('/cart');
        }, 1500);
      } else {
        showError('No items could be added to cart. Please check product availability.');
      }
    } catch (error) {
      console.error('Failed to reorder:', error);
      showError('Failed to add items to cart. Please try again.');
    }
  };






  const handleSort = (field: string) => {
    if (sortBy === field) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getPaymentMethodIcon = (method?: string): string => {
    if (!method) return 'question-circle';
    const methodMap: Record<string, string> = {
      'card': 'credit-card',
      'stripe': 'credit-card',
      'purchase_order': 'file-invoice',
      'po': 'file-invoice',
      'wire': 'university',
      'check': 'money-check'
    };
    return methodMap[method.toLowerCase()] || 'dollar-sign';
  };

  if (loading && orders.length === 0) {
    return (
      <Layout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading orders...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '20px 1.5rem'
      }}>
        <div className="row">
          <div className="col-12">
            <div className="mb-4">
              <h1 style={{
                fontSize: '28px',
                fontWeight: '600',
                color: '#2c3e50'
              }}>
                <i className="fas fa-box-open me-2"></i>Orders
              </h1>
            </div>

            {error && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                <i className="fas fa-exclamation-circle me-2"></i>{error}
                <button type="button" className="btn-close" onClick={() => setError('')}></button>
              </div>
            )}

            {orders.length === 0 ? (
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="fas fa-box-open fa-3x text-muted mb-3"></i>
                  <h4>No orders found</h4>
                  {hasActiveFilters ? (
                    <p className="text-muted">No orders match the selected filters.</p>
                  ) : (
                    <>
                      <p className="text-muted">You haven't placed any orders yet.</p>
                      <button
                        className="btn"
                        onClick={() => navigate('/products')}
                        style={{
                          backgroundColor: '#2E7D32',
                          color: 'white',
                          border: 'none',
                          padding: '10px 20px',
                          fontSize: '1rem'
                        }}
                      >
                        Start Shopping
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <>
                {hasActiveFilters && (
                  <div className="mb-3 d-flex justify-content-end">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        setStatusFilter([]);
                        setPaymentStatusFilter([]);
                      }}
                      style={{ fontSize: '0.875rem', padding: '6px 12px' }}
                    >
                      <i className="fas fa-times me-2"></i>Clear Filters
                    </button>
                  </div>
                )}
                <OrdersTable
                  orders={orders}
                  onReorder={handleReorder}
                  showAllLink={false}
                  compact={false}
                  showHeader={false}
                  showFilters={true}
                  statusFilter={statusFilter}
                  paymentStatusFilter={paymentStatusFilter}
                  onStatusFilterChange={setStatusFilter}
                  onPaymentStatusFilterChange={setPaymentStatusFilter}
                  availableStatuses={availableStatuses}
                  availablePaymentStatuses={availablePaymentStatuses}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />
                {/* Pagination */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Responsive Styles */}
      <style>{`
          @media (max-width: 768px) {
            .table-responsive table {
              font-size: 0.875rem;
            }
            
            .btn-group .btn {
              padding: 0.25rem 0.5rem;
              font-size: 0.75rem;
            }
            
            .badge {
              font-size: 0.7rem;
            }
          }
        `}</style>
    </Layout>
  );
};

export default OrderHistory;